import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Leader {
  id: number;
  name: string;
  position: string;
  expertise: string;
  experience: string;
  image: string;
  alt: string;
  credentials: string[];
}

interface LeadershipTeamProps {
  leaders: Leader[];
}

export default function LeadershipTeam({ leaders }: LeadershipTeamProps) {
  return (
    <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-primary mb-4">
            Leadership Team
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Meet the experienced professionals driving technical excellence and operational reliability at Ativanam Solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className="bg-card rounded-lg shadow-subtle overflow-hidden hover:shadow-modal transition-shadow duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={leader.image}
                  alt={leader.alt}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="p-6">
                <h3 className="text-xl font-headline font-bold text-textPrimary mb-1">
                  {leader.name}
                </h3>
                <p className="text-primary font-body font-semibold mb-3">
                  {leader.position}
                </p>

                <div className="mb-4">
                  <div className="flex items-start mb-2">
                    <Icon
                      name="BriefcaseIcon"
                      size={18}
                      className="text-secondary mt-1 mr-2 flex-shrink-0"
                    />
                    <p className="text-sm text-textSecondary">{leader.expertise}</p>
                  </div>
                  <div className="flex items-start">
                    <Icon
                      name="ClockIcon"
                      size={18}
                      className="text-secondary mt-1 mr-2 flex-shrink-0"
                    />
                    <p className="text-sm text-textSecondary">{leader.experience}</p>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-xs font-body font-semibold text-textPrimary mb-2">
                    Key Credentials:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {leader.credentials.map((credential, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded bg-surface text-xs text-textSecondary"
                      >
                        {credential}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}